import { useMemo } from "react";
import type { BalanceAge } from "api/responses";
import type { BulkRequestFilter } from "./BulkRequestBar/BulkRequestFilterBar/compileBulkRequestFilter";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";
import type { StrictlyBasisPoints } from "@vyne-shared/utils/currency";

/**
 * Apply a filter to bulk request rows.
 * @param rows Unfiltered data.
 * @param compiledFilter Compiled filter (generally obtained from compileBulkRequestFilter).
 * @returns Filtered data.
 */
export function useBulkRequestApplyFilter(
    rows: readonly BulkRequestRecord[] | undefined,
    compiledFilter: BulkRequestFilter,
) {
    return useMemo(() => (rows ? rows.filter((x) => matches(x, compiledFilter)) : undefined), [rows, compiledFilter]);
}

function matches(record: BulkRequestRecord, filter: BulkRequestFilter) {
    return (
        agingMatches(record.balanceAge, filter.agings) &&
        balanceMatches(record.pmsBalances.balanceEstimated, filter.minBalance) &&
        patientMatches(record.fullName, filter.patient) &&
        outstandingClaimsMatches(record.hasOutstandingClaims, filter.includeOutstandingClaims) &&
        activePaymentPlansMatches(record.hasActivePaymentPlan, filter.includeActivePaymentPlans)
    );
}

function agingMatches(record: BalanceAge, filter: Readonly<Set<BalanceAge>> | undefined) {
    return typeof filter === "undefined" || filter.has(record);
}

function balanceMatches(record: StrictlyBasisPoints, filter: StrictlyBasisPoints | undefined) {
    return typeof filter === "undefined" || record >= filter;
}

function patientMatches(record: string, filter: string) {
    return !filter || record.toLowerCase().includes(filter);
}

function outstandingClaimsMatches(record: boolean, filter: boolean) {
    // if filter is false, hide outstanding claims (!record), otherwise show all
    return (!filter && !record) || filter;
}

function activePaymentPlansMatches(record: boolean, filter: boolean | undefined) {
    // if filter is false, hide active payment plans (!record), otherwise show all
    return (!filter && !record) || filter;
}
